import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, Trophy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "./directory/states";
import TrailMapDialog from "./TrailMapDialog";

const SURFACE_CHIP = {
  Paved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Unpaved: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Mixed: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

export default function TopOpportunityPanel() {
  const [stateOptions, setStateOptions] = useState([]);
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [minMiles, setMinMiles] = useState("5");
  const [surface, setSurface] = useState("all");
  const [minRestaurants, setMinRestaurants] = useState("0");
  const [minBikeShops, setMinBikeShops] = useState("0");
  const [sortBy, setSortBy] = useState("restaurants_per_mile");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [openTrailId, setOpenTrailId] = useState(null);

  const codeToName = useMemo(() => Object.fromEntries(US_STATES.map(([c, n]) => [c, n])), []);

  useEffect(() => {
    api.get("/admin/trails/states").then(({ data }) => setStateOptions(data.states.filter((s) => s.available)))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const load = useCallback(async () => {
    if (!state) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trails", { params: {
        state, q: q.trim() || undefined, min_miles: Number(minMiles),
        surface: surface !== "all" ? surface : undefined, hide_numbered: true,
        min_restaurants: Number(minRestaurants), min_bike_shops: Number(minBikeShops), sort: sortBy,
      } });
      setRows(data.items);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [state, q, minMiles, surface, minRestaurants, minBikeShops, sortBy]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (!state) { setSummary(null); return; }
    api.get("/admin/trails/summary", { params: { state } })
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [state]);

  const NumCell = ({ v, cls = "" }) => <TableCell className={`text-right tabular-nums ${cls}`}>{v ?? 0}</TableCell>;

  return (
    <div className="space-y-4" data-testid="top-opportunity-panel">
      <div>
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-primary" />Top Opportunity Trails</h2>
        <p className="text-sm text-muted-foreground">Rank trails by raw commercial-potential metrics — no score, just the evidence.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="opp-summary-card">
          {[
            { label: "Market bike shops", value: summary.market_bike_shops, cls: "text-cyan-300", tid: "summary-market" },
            { label: "CRM bike shops", value: summary.crm_bike_shops, cls: "text-sky-300", tid: "summary-crm" },
            { label: "Partners", value: summary.partners, cls: "text-emerald-300", tid: "summary-partners" },
            { label: "Prospects", value: summary.prospects, cls: "text-amber-300", tid: "summary-prospects" },
            { label: "Uncaptured shops", value: summary.uncaptured_shops, cls: "text-violet-300", tid: "summary-uncaptured" },
            { label: "Market penetration", value: summary.market_penetration_pct != null ? `${summary.market_penetration_pct.toFixed(1)}%` : "—", cls: "text-teal-300", tid: "summary-penetration" },
          ].map((s) => (
            <div key={s.tid} className="rounded-2xl border border-border bg-card p-4" data-testid={s.tid}>
              <div className="text-xs text-muted-foreground">{s.label}</div>
              <div className={`text-2xl font-heading font-extrabold mt-1 tabular-nums ${s.cls}`}>{s.value ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-end gap-3">
        <div className="w-52">
          <div className="text-xs text-muted-foreground mb-1.5">State</div>
          <Select value={state || "none"} onValueChange={(v) => setState(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="opp-state-select"><SelectValue placeholder="Select a state" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="none">Select a state</SelectItem>
              {stateOptions.map((s) => <SelectItem key={s.state} value={s.state}>{s.name} ({s.available})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <div className="text-xs text-muted-foreground mb-1.5">Search</div>
          <Search className="w-4 h-4 absolute left-3 top-[34px] text-muted-foreground" />
          <Input className="pl-9" placeholder="Trail name" value={q} onChange={(e) => setQ(e.target.value)} disabled={!state} data-testid="opp-search" />
        </div>
        <div className="w-32">
          <div className="text-xs text-muted-foreground mb-1.5">Min length</div>
          <Select value={minMiles} onValueChange={setMinMiles}>
            <SelectTrigger data-testid="opp-minlength"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["0", "5", "10", "20"].map((v) => <SelectItem key={v} value={v}>{v === "0" ? "All lengths" : `${v}+ mi`}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <div className="text-xs text-muted-foreground mb-1.5">Surface</div>
          <Select value={surface} onValueChange={setSurface}>
            <SelectTrigger data-testid="opp-surface"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paved">Paved</SelectItem>
              <SelectItem value="unpaved">Unpaved</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <div className="text-xs text-muted-foreground mb-1.5">Min restaurants</div>
          <Select value={minRestaurants} onValueChange={setMinRestaurants}>
            <SelectTrigger data-testid="opp-min-restaurants"><SelectValue /></SelectTrigger>
            <SelectContent>{["0", "5", "10", "25", "50", "100"].map((v) => <SelectItem key={v} value={v}>{v === "0" ? "Any" : `${v}+`}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <div className="text-xs text-muted-foreground mb-1.5">Min bike shops</div>
          <Select value={minBikeShops} onValueChange={setMinBikeShops}>
            <SelectTrigger data-testid="opp-min-bikeshops"><SelectValue /></SelectTrigger>
            <SelectContent>{["0", "1", "2", "5", "10"].map((v) => <SelectItem key={v} value={v}>{v === "0" ? "Any" : `${v}+`}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <div className="text-xs text-muted-foreground mb-1.5">Rank by</div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="opp-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="restaurants_per_mile">Most restaurants / mile</SelectItem>
              <SelectItem value="bike_shops_per_mile">Most bike shops / mile</SelectItem>
              <SelectItem value="uncaptured">Most uncaptured opportunity</SelectItem>
              <SelectItem value="penetration">Highest market penetration %</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {!state ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="opp-empty">Select a state to rank its trails by opportunity.</div>
        ) : loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="opp-empty">No trails match these filters. Try lowering the minimums.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Trail</TableHead>
                  <TableHead className="text-right">Length</TableHead>
                  <TableHead>Surface</TableHead>
                  <TableHead className="text-right text-orange-300">Restaurants</TableHead>
                  <TableHead className="text-right text-orange-300">/mi</TableHead>
                  <TableHead className="text-right text-cyan-300">Market shops</TableHead>
                  <TableHead className="text-right text-cyan-300">/mi</TableHead>
                  <TableHead className="text-right text-emerald-300">Partner</TableHead>
                  <TableHead className="text-right text-amber-300">Prospect</TableHead>
                  <TableHead className="text-right text-violet-300">Uncaptured</TableHead>
                  <TableHead className="text-right text-teal-300">Market Penetration %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t, i) => (
                  <TableRow key={t.id} data-testid={`opp-row-${t.id}`}>
                    <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium max-w-[220px] truncate">
                      <div className="flex items-center gap-1.5">
                        {t.status === "saved" && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" title="Saved to Bike Paths" />}
                        <button type="button" onClick={() => setOpenTrailId(t.id)} className="text-left text-primary hover:underline truncate w-full" data-testid={`opp-trail-name-${t.id}`} title="Preview trail on map">{t.name}</button>
                      </div>
                    </TableCell>
                    <NumCell v={`${t.length_miles} mi`} />
                    <TableCell>{t.surface_class && t.surface_class !== "Unknown" ? <Badge className={`border text-[10px] px-1.5 py-0 ${SURFACE_CHIP[t.surface_class] || ""}`}>{t.surface_class}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <NumCell v={t.restaurant_count} cls="text-orange-300 font-semibold" />
                    <NumCell v={t.restaurants_per_mile} cls="text-orange-300" />
                    <NumCell v={t.market_bike_shop_count} cls="text-cyan-300 font-semibold" />
                    <NumCell v={t.market_bike_shops_per_mile} cls="text-cyan-300" />
                    <NumCell v={t.partner_shop_count} cls="text-emerald-300" />
                    <NumCell v={t.prospect_shop_count} cls="text-amber-300" />
                    <NumCell v={t.uncaptured_market_opportunity} cls="text-violet-300 font-bold" />
                    <TableCell className="text-right tabular-nums text-teal-300">{t.market_penetration_pct != null ? `${t.market_penetration_pct.toFixed(1)}%` : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {state && rows.length > 0 && <p className="text-xs text-muted-foreground">{rows.length} trails · ranked by {sortBy.replace(/_/g, " ")}. These are raw metrics — no opportunity score is applied. Click a trail name to preview it on the map.</p>}
      <TrailMapDialog trailId={openTrailId} open={!!openTrailId} onOpenChange={(o) => { if (!o) setOpenTrailId(null); }} onSaved={load} />
    </div>
  );
}
